import React, { useEffect, useState } from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  FlatList,
  Platform,
  RefreshControl} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import { RouteProp, useRoute } from "@react-navigation/native";
import SkeletonLoader from "../../../components/SkeletonLoader";
import FormattedText from "../../../components/FormattedText";
import commonStyles from "../../../styles/commonStyles";
import OverflowTooltipText from "../../../components/OverflowTooltipText";
import { InvestmentModel } from "../../../types/IInvestmentTypes";
import { IEntityPropertyModel, IPropertyData } from "../../../types/IPropertyDataType";
import { getUserIdToUse } from "../../../utils/getUserIdToUse";
import { SafeAreaView } from "react-native-safe-area-context";
import AppText from "../../../components/AppText";
export interface InvestmentDetailsProps {
  navigation: {
    navigate: (screen: string, params?: { [key: string]: any }) => void;
    goBack: () => void;
  };
}
const InvestmentDetails: React.FC<InvestmentDetailsProps> = ({
  navigation}) => {
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [propertyData, setPropertyData] = useState<IEntityPropertyModel[]>([]);
  const [refreshing, setRefreshing] = useState(false);

  const route =
    useRoute<RouteProp<{ params: { item: InvestmentModel } }, "params">>();
  const investments = route.params?.item;
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };
  const fetchInvestmentData = async () => {
    console.log("the Item I got ... ", investments);
    try {
      if (investments.investedProperties) {
        investments.investedProperties.forEach((item) => {
          item.yourAcres =
            (item.totalAcres * item.shareholderOwnership) / 100;
          item.remainingAcres =
            (item.remainingAcres * item.shareholderOwnership) / 100;
          const roundedAcres = roundToTwoDecimals(item.remainingAcres);
          const roundedPrice = roundToTwoDecimals(item.potentialPricePerAcres);

          item.potentialMarketValue = roundToTwoDecimals(
            roundedAcres * roundedPrice
          );
        });
        setPropertyData(investments.investedProperties);
      }
      setLoadingProperties(false);
    } catch (error) {
      setError("Error fetching data");
      setLoadingProperties(false);
    }
  };

  useEffect(() => {
    fetchInvestmentData();
  }, []);

  const handleRefresh = () => {
    fetchInvestmentData();
  };

  const renderPropertyItem = ({ item }: { item: IEntityPropertyModel }) => (
    <View
      style={[
        commonStyles.subContainer,
        commonStyles.mb10,
        commonStyles.p14,
        commonStyles.mt10,
      ]}
    >
      <TouchableOpacity
        testID="Property-Name"
        onPress={() =>
          navigation.navigate("PropertyDetails", {
            property: item,
            shareholderName: investments.shareholderName,
            isInvestment: true,
            sourcePage: "InvestmentDetails"})
        }
      > 
        <View style={commonStyles.cardItemblock}>
          <OverflowTooltipText
            testID={`property-name-${item.propertyId}`}
            text={item.name}
            style={commonStyles.detailsSubHeader}
            onPress={() =>
              navigation.navigate("PropertyDetails", {
                property: item,
                shareholderName: investments.shareholderName,
                isInvestment: true,
                sourcePage: "InvestmentDetails"})
            }
          />
          <AntDesign
            testID={`right-arrow-${item.propertyId}`}
            name="arrow-right"
            size={22}
            color="#666"
            marginBottom={4}
          />
        </View>
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView edges={[ "bottom", "left", "right"]} style={[commonStyles.flex1, commonStyles.screenBottomGap]}>
      <View style={[commonStyles.container]}>
        <TouchableOpacity
          testID="EntityName"
          onPress={() => navigation.goBack()}
          style={commonStyles.subHeaderContainer}
        >
          <AntDesign testID="leftArrow" name="left" size={24} color="black" />
          <OverflowTooltipText
            testID="Entity-Name"
            text={investments?.entityName}
            style={commonStyles.subHeader}
            onPress={() => navigation.goBack()}
          />
        </TouchableOpacity>
        <ScrollView
          testID="scrollView"
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />
          }
        >
          <View
            style={[
              commonStyles.subContainer,
              commonStyles.p14,
              commonStyles.mlar2,
              commonStyles.mt5,
              commonStyles.pt0,
              commonStyles.mb10,
            ]}
          >
            <View style={commonStyles.columnContainer}>
              <AppText testID="shareholderName" style={commonStyles.sectionLabel}>
                Partner Name
              </AppText>
              <View style={{ width: "100%", alignItems: "flex-end" }}>
              <OverflowTooltipText
                testID="ShareholderName"
                text={investments?.shareholderName}
                style={commonStyles.valueText}
              />
              </View>
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="investment" style={commonStyles.subTitle}>
                Your Investment
              </AppText>
              {/* <AppText style={InvestmentDetailsStyle.value}>
                ${item?.investment?.toLocaleString()}
              </AppText> */}
              <FormattedText
                testID="Investment"
                value={investments.investment}
                type="currency"
                style={commonStyles.subValue}
              />
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="totalCash" style={commonStyles.subTitle}>
                Total Cash
              </AppText>

              <FormattedText
                testID="TotalCash"
                value={investments.totalDownpayment}
                type="currency"
                style={commonStyles.subValue}
              />
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="loan" style={commonStyles.subTitle}>
                Initial Loan
              </AppText>
              <FormattedText
                testID="Loan"
                value={investments.loan}
                type="currency"
                style={commonStyles.subValue}
              />
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="currentQemi" style={commonStyles.subTitle}>
                Current QEMI
              </AppText>
              <FormattedText
                testID="CurrentQEMI"
                value={investments?.revisedQemi ?? 0}
                type="currency"
                style={commonStyles.subValue}
              />
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="revisedLoan" style={commonStyles.subTitle}>
                Current Loan
              </AppText>

              <FormattedText
                testID="RevisedLoan"
                value={investments.revisedLoan}
                type="currency"
                style={commonStyles.subValue}
              />
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="totalShare" style={commonStyles.subTitle}>
                Total Shares
              </AppText>

              <FormattedText
                testID="TotalShares"
                value={investments.totalShare}
                type="number"
                style={commonStyles.subValue}
              />
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="yourShare" style={commonStyles.subTitle}>
                Your Shares
              </AppText>
              <FormattedText
                testID="YourShares"
                value={investments.shares}
                type="number"
                style={commonStyles.subValue}
              />
            </View>
            <View style={commonStyles.subDetails}>
              <AppText testID="originalQemi" style={commonStyles.subTitle}>
                Original QEMI
              </AppText>
              <FormattedText
                testID="OriginalQEMI"
                value={investments.qemi}
                type="currency"
                style={commonStyles.subValue}
              />
            </View>
          </View>

          <AppText testID="property" style={commonStyles.detailsHeader}>
            Properties
          </AppText>

          <FlatList
            testID="Properties"
            data={propertyData}
            scrollEnabled={Platform.OS == "web" ? true : false}
            renderItem={renderPropertyItem}
            keyExtractor={(property) =>
              property.propertyId?.toString() || Math.random().toString()
            }
            ListEmptyComponent={() =>
              loadingProperties ? (
                <View testID="skeleton-loader">
                <SkeletonLoader rows={5} />
                </View>
              ) : (
                <AppText testID="emptyProperty" style={[commonStyles.emptyData]}>
                  No properties available
                </AppText>
              )
            }
          />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default InvestmentDetails;
